import { useState, useEffect } from "react";
import {
  RefreshCw,
  AlertTriangle,
  ArrowRight,
  Settings,
  Monitor,
  LayoutGrid,
  Laptop,
  Smartphone,
  Tablet,
  Earth,
  CircleCheck,
  Link,
  HomeIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import TroubleshootingStep from "./TroubleshootingStep";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { BsFillInfoCircleFill } from "react-icons/bs";
import { IoIosWarning } from "react-icons/io";
import { useLocalStorage, safeStorage } from "@/lib/storage";
import { Breadcrumb, BreadcrumbItem } from "./ui/breadcrumb";

interface TroubleshootingWizardProps {
  onReset: () => void;
  onContactSupport: (completedSteps: number[]) => void;
  hasVideoError?: boolean;
}

const TroubleshootingWizard = ({
  onReset,
  onContactSupport,
  hasVideoError = false,
}: TroubleshootingWizardProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useLocalStorage<number[]>(
    "completedTroubleshootingSteps",
    []
  );
  const [showAdvancedSteps, setShowAdvancedSteps] = useState(false);

  // Load active step from storage on mount
  useEffect(() => {
    const savedActiveStep = safeStorage.get("activeTroubleshootingStep");
    if (savedActiveStep !== null) {
      setActiveStep(parseInt(savedActiveStep));
    }
  }, []);

  // Save active step whenever it changes
  useEffect(() => {
    safeStorage.set("activeTroubleshootingStep", activeStep.toString());
  }, [activeStep]);

  const basicSteps = [
    {
      stepNumber: 1,
      title: "Try a Different Device or Browser",
      description: "Test if the issue is specific to your current setup.",
      instructions:
        "If possible, try watching on a different device (phone, tablet) or using another browser (Chrome, Firefox, Safari, Edge).",
      completedText: "I've tried another device/browser",
      actionText: "I've tried another device/browser",
    },
    {
      stepNumber: 2,
      title: "Update Your Browser",
      description: "Outdated browsers may have compatibility issues.",
      instructions:
        "Make sure your browser is updated to the latest version available.",
      actionText: "Check for Browser Updates",
      completedText: "My browser is updated",
    },
    {
      stepNumber: 3,
      title: "Try Incognito/Private Mode",
      description: "Test if your browser configuration is causing issues.",
      instructions:
        "Open a new incognito or private browsing window and try watching the video there.",
      completedText: "I've tried incognito mode",
    },
    {
      stepNumber: 4,
      title: "Reset Your Browser",
      description: (
        <div className="space-y-4">
          <div className="text-lg max-sm:text-base">
            If you see a black box with a blue play button but nothing happens
            when clicked, try resetting your browser settings. You can see below
            for more info.
          </div>

          <div className="grid grid-cols-2 max-sm:grid-cols-1 lg:grid-cols-4 gap-4">
            <a
              href="https://support.google.com/chrome/answer/3296214"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-inherit hover:text-inherit h-full cursor-pointer transition-all duration-200 hover:scale-[1.02]"
            >
              <div className="h-full max-w-xl bg-muted border border-border hover:border-accent/40 p-6 max-sm:p-4 rounded-xl hover:bg-accent/10 group flex flex-col shadow-sm hover:shadow-md">
                <div className="p-2 max-w-[40px] max-h-[40px] bg-accent/20 group-hover:bg-accent/30 rounded-lg flex items-center justify-center mb-3">
                  <Earth
                    size={30}
                    className="text-accent-medium group-hover:text-accent-medium"
                  />
                </div>
                <div className="flex-1">
                  <h4 className="text-base text-foreground font-[500] mb-1">
                    For Chrome
                  </h4>
                  <div className="text-sm text-muted-foreground">
                    Reset Chrome settings and clear cache
                  </div>
                </div>
              </div>
            </a>
            <a
              href="https://support.apple.com/guide/safari/clear-your-browsing-history-sfri47acf5d6/mac"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-inherit hover:text-inherit h-full cursor-pointer transition-all duration-200 hover:scale-[1.02]"
            >
              <div className="h-full max-w-xl bg-muted border border-border hover:border-accent/40 p-6 rounded-xl hover:bg-accent/10 group flex flex-col shadow-sm hover:shadow-md">
                <div className="p-2 max-w-[40px] max-h-[40px] bg-accent/20 group-hover:bg-accent/30 rounded-lg flex items-center justify-center mb-3">
                  <Earth
                    size={30}
                    className="text-accent-medium group-hover:text-accent-medium"
                  />
                </div>
                <div className="flex-1">
                  <h4 className="text-base text-foreground font-[500] mb-1">
                    For Safari
                  </h4>
                  <div className="text-sm text-muted-foreground">
                    Clear website data and reset settings
                  </div>
                </div>
              </div>
            </a>
            <a
              href="https://support.mozilla.org/en-US/kb/refresh-firefox-reset-add-ons-and-settings"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-inherit hover:text-inherit h-full cursor-pointer transition-all duration-200 hover:scale-[1.02]"
            >
              <div className="h-full max-w-xl bg-muted border border-border hover:border-accent/40 p-6 rounded-xl hover:bg-accent/10 group flex flex-col shadow-sm hover:shadow-md">
                <div className="p-2 max-w-[40px] max-h-[40px] bg-accent/20 group-hover:bg-accent/30 rounded-lg flex items-center justify-center mb-3">
                  <Earth
                    size={30}
                    className="text-accent-medium group-hover:text-accent-medium"
                  />
                </div>
                <div className="flex-1">
                  <h4 className="text-base text-foreground font-[500] mb-1">
                    For Firefox
                  </h4>
                  <div className="text-sm text-muted-foreground">
                    Refresh Firefox and clear cookies
                  </div>
                </div>
              </div>
            </a>
            <a
              href="https://www.lifewire.com/reset-microsoft-edge-5101664"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-inherit hover:text-inherit h-full cursor-pointer transition-all duration-200 hover:scale-[1.02]"
            >
              <div className="h-full max-w-xl bg-muted border border-border hover:border-accent/40 p-6 rounded-xl hover:bg-accent/10 group flex flex-col shadow-sm hover:shadow-md">
                <div className="p-2 max-w-[40px] max-h-[40px] bg-accent/20 group-hover:bg-accent/30 rounded-lg flex items-center justify-center mb-3">
                  <Earth
                    size={30}
                    className="text-accent-medium group-hover:text-accent-medium"
                  />
                </div>
                <div className="flex-1">
                  <h4 className="text-base text-foreground font-[500] mb-1">
                    For Edge
                  </h4>
                  <div className="text-sm text-muted-foreground">
                    Reset Edge settings and clear cache
                  </div>
                </div>
              </div>
            </a>
          </div>
          <div className="bg-primary/5 border-l-4 border-primary p-4 max-sm:p-3 flex items-start gap-3">
            <BsFillInfoCircleFill size={15} className="text-primary" />
            <div className="text-sm text-primary">
              For the best learning experience on our website, we recommend
              Chrome.
            </div>
          </div>
          <div className="text-lg max-sm:text-base mb-6 mt-6">
            After you confirm that you've reset your browser, your screen will
            reload automatically.
          </div>
        </div>
      ),
      // instructions:
      //   "Reset your browser settings and clear cache to resolve video playback issues.",
      completedText: "I've reset my browser",
    },
    {
      stepNumber: 5,
      title: "Whitelist Nicole the Math Lady Domains",
      description: (
        <div>
          <div className="text-lg max-sm:text-base mb-6">
            You may have parental control settings that are not allowing you to
            see the video. Add these domains to your allowed list to ensure
            video content loads properly.
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 max-sm:gap-3 bg-muted p-4 max-sm:p-3 border border-border rounded-xl mb-6">
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.gumlet.io</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.gumlet.tv</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.cdn.gumlet.com</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground"> *.gumlet.com</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.iframe.mediadelivery.net</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.video.bunnycdn.com</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.nicolethemathlady.com</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground"> *.mediadelivery.net</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">
                *.api.nicolethemathlady.com
              </span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.vhx.tv</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.vimeo.com</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.vimeocdn.com</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.akamaized.net</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.cloudfront.net</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.gumlytics.com</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.b-cdn.net</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.bunnyinfra.net</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.bunny.net</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">metrics-bunny.net</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">fonts.googleapis.com</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.gstatic.com</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.jsdelivr.net</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">static.cloudflareinsights.com</span>
            </li>
            <li className="text-sm flex items-center gap-2">
              <CircleCheck size={15} className="text-muted-foreground/60" />
              <span className="text-muted-foreground">*.sentry.io</span>
            </li>
          </ul>
          <div className="bg-warning/10 border-l-4 border-warning p-4 max-sm:p-3 flex items-start gap-3">
            <IoIosWarning size={20} className="text-warning" />
            <div className="text-sm text-warning">
              If you're on a managed network (school or workplace), you may need
              to ask your IT administrator to whitelist these domains.
            </div>
          </div>
          <div className="text-lg max-sm:text-base mb-6 mt-6">
            After you confirm that you've whitelisted the domains, your screen
            will reload automatically.
          </div>
        </div>
      ),
      // instructions:
      //   "Add these domains to your allowed list to ensure video content loads properly.",
      completedText: "I've whitelisted the domains",
    },
  ];

  // const advancedSteps = [
  //   {
  //     stepNumber: 30,
  //     title: "Disable Browser Extensions",
  //     description: "Some extensions may interfere with video playback.",
  //     instructions:
  //       "Try disabling all browser extensions/add-ons, especially ad blockers, privacy tools, or script blockers.",
  //     actionText: "How to Manage Extensions",
  //     actionUrl: "#",
  //     completedText: "I've disabled extensions",
  //   },
  //   {
  //     stepNumber: 40,
  //     title: "Try Incognito/Private Mode",
  //     description: "Test if your browser configuration is causing issues.",
  //     instructions:
  //       "Open a new incognito or private browsing window and try watching the video there.",
  //     completedText: "I've tried incognito mode",
  //   },
  //   {
  //     stepNumber: 50,
  //     title: "Try a Different Device or Browser",
  //     description: "Test if the issue is specific to your current setup.",
  //     instructions:
  //       "If possible, try watching on a different device (phone, tablet) or using another browser (Chrome, Firefox, Safari, Edge).",
  //     completedText: "I've tried another device/browser",
  //   },
  //   {
  //     stepNumber: 6,
  //     title: "Check Network/IT Settings",
  //     description: "School or work networks often restrict video content.",
  //     instructions:
  //       "If you're on a school or work network, ask your IT administrator to review network settings for video streaming permissions.",
  //     completedText: "I'll check with IT admin",
  //   },
  //   {
  //     stepNumber: 7,
  //     title: "Review Parental Controls",
  //     description: "Parental control software may block video streaming.",
  //     instructions:
  //       "Check if any parental control software is installed on your device that might be restricting video content.",
  //     completedText: "I've checked parental controls",
  //   },
  //   {
  //     stepNumber: 8,
  //     title: "Update Your Browser",
  //     description: "Outdated browsers may have compatibility issues.",
  //     instructions:
  //       "Make sure your browser is updated to the latest version available.",
  //     actionText: "Check for Browser Updates",
  //     actionUrl: "#",
  //     completedText: "My browser is updated",
  //   },
  // ];

  const advancedSteps = [];

  const steps = [...basicSteps, ...(showAdvancedSteps ? advancedSteps : [])];

  const handleStepComplete = (index: number) => {
    // console.log("completedSteps", JSON.parse(completedSteps));
    // const newCompletedSteps = [...JSON.parse(completedSteps), index];
    const newCompletedSteps = [...completedSteps, index];
    setCompletedSteps(newCompletedSteps);
    // console.log("newCompletedSteps", newCompletedSteps);
    safeStorage.set("completedTroubleshootingSteps", newCompletedSteps);

    // If the "Reset Your Browser" step is completed (index 3), trigger a video retry
    if (index === 3) {
      // Set the next step as active before resetting
      const nextStep = 4;
      setActiveStep(nextStep);
      safeStorage.set("activeTroubleshootingStep", nextStep.toString());
      onReset();
      return;
    }

    //add onReset(); on step 4
    if (index === 4) {
      // Set the next step as active before resetting
      const nextStep = 5;
      setActiveStep(nextStep);
      safeStorage.set("activeTroubleshootingStep", nextStep.toString());
      onReset();
      return;
    }

    // If all basic steps completed and advanced steps not shown yet, show advanced steps
    if (index === basicSteps.length - 1 && !showAdvancedSteps) {
      setShowAdvancedSteps(true);
      setActiveStep(basicSteps.length);
      safeStorage.set(
        "activeTroubleshootingStep",
        basicSteps.length.toString()
      );
      return;
    }

    // If all steps completed, stay on the last step
    if (index === steps.length - 1) {
      return;
    }

    // Move to next step
    const nextStep = index + 1;
    setActiveStep(nextStep);
    safeStorage.set("activeTroubleshootingStep", nextStep.toString());

    // Smooth scroll to the next step
    setTimeout(() => {
      const nextStepElement = document.querySelector(
        `[data-step="${nextStep}"]`
      );
      if (nextStepElement) {
        const headerOffset = 80; // 64px header + 16px breathing room
        const elementPosition = nextStepElement.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: elementPosition - headerOffset, behavior: "smooth" });
      }
    }, 100);
  };

  const handleSkip = () => {
    // If on last step, stay there
    if (activeStep === steps.length - 1) {
      return;
    }

    // Otherwise, move to next step
    setActiveStep(activeStep + 1);
  };

  const handleReset = () => {
    // Clear all storage items for a full reset
    safeStorage.remove("completedTroubleshootingSteps");
    safeStorage.remove("activeTroubleshootingStep");
    setCompletedSteps([]);
    setActiveStep(0);
    setShowAdvancedSteps(false);
    onReset();
  };

  const allBasicStepsCompleted = basicSteps.every((_, index) => {
    return completedSteps.includes(index);
  });
  const allStepsCompleted = steps.every((_, index) =>
    completedSteps.includes(index)
  );

  return (
    <div className="space-y-8 animate-fade-in">
      {hasVideoError && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 flex flex-col items-center text-center">
          <div className="size-12 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center mb-4">
            <AlertTriangle className="h-6 w-6 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Video Playback Issue Detected
          </h3>
          <p className="text-sm text-gray-500 max-w-md leading-relaxed">
            We've detected that the video isn't playing correctly. Follow the
            steps below to diagnose and fix the issue.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {/* Basic troubleshooting steps */}



        {basicSteps.map((step, index) => (
          <div key={index} data-step={index}>
            <TroubleshootingStep
              {...step}
              isActive={activeStep === index || completedSteps.includes(index)}
              isCompleted={completedSteps.includes(index)}
              instructions={step.instructions || ""}
              actionText={step.actionText || ""}
              onComplete={() => handleStepComplete(index)}
              onSkip={handleSkip}
            />
          </div>
        ))}

        {/* Advanced troubleshooting section */}
        {/* {(showAdvancedSteps || allBasicStepsCompleted) && (
          <div className="space-y-4 mt-8">
            <h3 className="font-semibold text-primary text-lg flex items-center gap-2 ml-1">
              <Monitor className="h-5 w-5" />
              Advanced Troubleshooting
            </h3>

            {advancedSteps.map((step, index) => {
              const actualIndex = index + basicSteps.length;
              return (
                <TroubleshootingStep
                  key={actualIndex}
                  {...step}
                  isActive={
                    activeStep === actualIndex ||
                    completedSteps.includes(actualIndex)
                  }
                  onComplete={() => handleStepComplete(actualIndex)}
                  onSkip={handleSkip}
                />
              );
            })}
          </div>
        )} */}
      </div>

      <div
        className={cn(
          "p-6 border border-primary/20 rounded-lg bg-primary/5 space-y-4",
          allBasicStepsCompleted || allStepsCompleted
            ? "animate-fade-in"
            : "opacity-80"
        )}
      >
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <LayoutGrid className="h-5 w-5 text-primary" />
          {allStepsCompleted
            ? "Still having video issues?"
            : allBasicStepsCompleted
              ? "Need more help?"
              : "Need immediate help?"}
        </h3>

        <p className="text-sm text-muted-foreground">
          {allStepsCompleted
            ? "We're sorry you're still experiencing problems. Our support team is ready to help solve this issue."
            : allBasicStepsCompleted
              ? "Try the advanced troubleshooting steps or contact our support team directly."
              : "You can skip the troubleshooting steps and contact our support team directly."}
        </p>

        <div className="flex flex-wrap gap-3 pt-2 max-sm:flex-col">
          <Button
            className="bg-primary hover:bg-primary/90 shadow-sm max-sm:w-full"
            onClick={() => onContactSupport(completedSteps)}
          >
            <ArrowRight className="mr-2 h-4 w-4" />
            Contact Support
          </Button>

          <Button
            variant="outline"
            className="border-primary/30 text-primary hover:bg-primary/10 max-sm:w-full"
            onClick={handleReset}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Try Video Again
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TroubleshootingWizard;
